import { formatCurrency } from '../../../utils/currency';

/**
 * Provider chip — shared component #1 (README "Shared components" list).
 * Source: Quote Flow Components.dc.html §1a.
 *
 * Real-data note: the design's 5 states include `viewed` (a read-receipt),
 * which this codebase has no tracking for — there is no view-event anywhere
 * in the job/quote data model. This component still accepts it for when
 * that tracking lands, but nothing in this run ever passes it (see PR
 * description judgment calls).
 */
export type ProviderChipState =
  | 'quoted'
  | 'viewed'
  | 'not-yet'
  | 'declined'
  | 'booked';

interface ProviderChipProps {
  name: string;
  /** Service title shown directly under the business name in detail-list variant */
  serviceTitle?: string;
  state: ProviderChipState;
  /** Shown instead of the state word when state === 'quoted'. */
  price?: number;
  /** 'chip' = 22px pill (S6 request cards). 'detail-list' = 34px row (S7). */
  variant?: 'chip' | 'detail-list';
  /** detail-list only — secondary tag (e.g., 'Itemised quote' vs 'Flat price'). */
  meta?: string;
  onClick?: () => void;
  className?: string;
}

const initials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

const CHIP_AVATAR_BG: Record<ProviderChipState, string> = {
  quoted: 'bg-[#224860]',
  viewed: 'bg-gray-500',
  'not-yet': 'bg-gray-400',
  declined: 'bg-gray-200 text-gray-500',
  booked: 'bg-[#15803D]',
};

const CHIP_STATE_TEXT: Record<ProviderChipState, string> = {
  quoted: 'text-[#0B6E78] font-semibold',
  viewed: 'text-gray-500',
  'not-yet': 'text-gray-400',
  declined: 'text-[#B91C1C]',
  booked: 'text-[#15803D] font-semibold',
};

const STATE_LABEL: Record<ProviderChipState, string> = {
  quoted: 'quoted',
  viewed: 'viewed',
  'not-yet': 'not yet',
  declined: 'declined',
  booked: 'booked',
};

// Detail-list variant uses a narrower, real-data-only palette
const DETAIL_STATE_TEXT: Record<ProviderChipState, string> = {
  quoted: 'text-[#16A34A]',
  viewed: 'text-gray-400',
  'not-yet': 'text-gray-400',
  declined: 'text-[#B91C1C]',
  booked: 'text-[#16A34A]',
};

const ProviderChip = ({
  name,
  serviceTitle,
  state,
  price,
  variant = 'chip',
  meta,
  onClick,
  className = '',
}: ProviderChipProps) => {
  const clickable = Boolean(onClick);
  const Wrapper = clickable ? 'button' : 'div';

  if (variant === 'detail-list') {
    return (
      <Wrapper
        type={clickable ? 'button' : undefined}
        onClick={onClick}
        className={`flex items-center gap-3 w-full text-left ${
          clickable ? 'cursor-pointer hover:bg-gray-50 rounded-xl' : ''
        } p-2.5 bg-slate-50/70 border border-slate-200/60 rounded-xl ${className}`}
      >
        <span className="w-[34px] h-[34px] rounded-full bg-[#224860] text-white text-xs font-semibold flex items-center justify-center shrink-0">
          {initials(name)}
        </span>

        {/* Left Section: Name + Service Title */}
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold text-gray-800 truncate">
            {name}
          </span>
          {serviceTitle && (
            <span className="block text-xs text-gray-500 font-medium truncate mt-0.5">
              {serviceTitle}
            </span>
          )}
        </span>

        {/* Right Section: Inline Meta Badge + Price/Status */}
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          {meta && (
            <span className="text-[10px] font-bold text-slate-500 bg-slate-200/60 border border-slate-300/60 px-2 py-0.5 rounded-md uppercase tracking-wide">
              {meta}
            </span>
          )}
          <span
            className={`text-xs font-bold ${
              state === 'quoted' && typeof price === 'number'
                ? 'bg-emerald-50 border border-emerald-200 text-[#16A34A] px-2.5 py-1 rounded-lg'
                : DETAIL_STATE_TEXT[state]
            }`}
          >
            {state === 'quoted' && typeof price === 'number'
              ? formatCurrency(price)
              : STATE_LABEL[state]}
          </span>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      type={clickable ? 'button' : undefined}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border pl-1 pr-2.5 py-0.5 text-xs ${
        state === 'booked'
          ? 'border-[#DCFCE7] bg-[#F6FEF9]'
          : 'border-gray-200 bg-white'
      } ${state === 'declined' ? 'opacity-70' : ''} ${
        clickable ? 'cursor-pointer' : ''
      } ${className}`}
    >
      <span
        className={`w-[22px] h-[22px] rounded-full text-white text-[9px] font-semibold flex items-center justify-center shrink-0 ${CHIP_AVATAR_BG[state]}`}
      >
        {initials(name)}
      </span>
      <span className="text-gray-700 truncate max-w-[110px]">{name}</span>
      <span className="text-gray-300">&middot;</span>
      <span className={CHIP_STATE_TEXT[state]}>
        {state === 'quoted' && typeof price === 'number'
          ? formatCurrency(price)
          : STATE_LABEL[state]}
      </span>
    </Wrapper>
  );
};

export default ProviderChip;
